/**
 * Side-by-side diff of two script revisions, used by the version-
 * history flow on the unified Tests page.
 *
 * Wraps `@monaco-editor/react`'s `DiffEditor` — same CDN-loaded
 * Monaco runtime that `MonacoCodePane` uses. Lazy-imported via
 * `React.lazy()` so we don't double-bundle Monaco; the first time
 * the editor pane mounts pays the network cost, subsequent renders
 * are instant.
 *
 * Always read-only. Editing a historical revision in place would
 * make no sense — restore (fast-follow PR) is the supported write
 * path back to the current version.
 */
import { lazy, Suspense, useEffect, useState } from 'react'
import { configureMonacoForPlaywright, isPlaywrightLanguage } from './monacoPlaywrightSetup'

const DiffEditor = lazy(() =>
  import('@monaco-editor/react').then(m => ({ default: m.DiffEditor })),
)

/** Mirrors `useIsDarkMode` from MonacoCodePane (kept local to avoid
 * a one-off shared module for two callers). Observes the `dark`
 * class on `<html>` and re-renders when it flips. */
function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  )
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const update = () => setIsDark(root.classList.contains('dark'))
    update()
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return isDark
}

export interface DiffPaneProps {
  /** Left-hand (older / "selected") revision contents. */
  original: string
  /** Right-hand (current / HEAD) revision contents. */
  modified: string
  /** Label shown over the left column — typically `<sha> · <phase>`. */
  originalLabel: string
  /** Label shown over the right column — typically `HEAD · current`. */
  modifiedLabel: string
  /** Monaco language id. Defaults to typescript (Playwright specs). */
  language?: string
}

export function DiffPane({
  original,
  modified,
  originalLabel,
  modifiedLabel,
  language = 'typescript',
}: DiffPaneProps) {
  const isDark = useIsDarkMode()
  return (
    <div className="h-full flex flex-col">
      <div className="flex-none grid grid-cols-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        <div className="px-3 py-1 border-r">{originalLabel}</div>
        <div className="px-3 py-1">{modifiedLabel}</div>
      </div>
      <div className="flex-1 min-h-0">
        <Suspense
          fallback={
            <div className="h-full grid grid-cols-2 text-xs font-mono">
              <pre className="overflow-auto p-3 bg-muted/30 whitespace-pre-wrap border-r">{original}</pre>
              <pre className="overflow-auto p-3 bg-muted/30 whitespace-pre-wrap">{modified}</pre>
            </div>
          }
        >
          <DiffEditor
            height="100%"
            language={language}
            original={original}
            modified={modified}
            theme={isDark ? 'vs-dark' : 'vs'}
            beforeMount={(monaco) => {
              if (isPlaywrightLanguage(language)) configureMonacoForPlaywright(monaco)
            }}
            options={{
              readOnly: true,
              renderSideBySide: true,
              fontSize: 12,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              lineNumbers: 'on',
              // Active-line highlight stays 'none' here (unlike the main
              // editor's 'line') — the diff already tints changed lines,
              // and a read-only diff has no caret to track.
              renderLineHighlight: 'none',
              smoothScrolling: true,
              // Shares the structural readability options with
              // MonacoCodePane: sticky scroll, bracket colorization,
              // indent guides.
              stickyScroll: { enabled: true },
              bracketPairColorization: { enabled: true },
              guides: { bracketPairs: true, indentation: true },
              folding: true,
              glyphMargin: false,
              // Original (left) pane is also read-only — DiffEditor
              // exposes a separate option for it that defaults to
              // false; force true so a stray click can't put a
              // caret in the older revision.
              originalEditable: false,
            }}
          />
        </Suspense>
      </div>
    </div>
  )
}
